import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { formatPrice } from "@di-devs/utils-fe";
import { Badge, Card, Divider, Image, Tooltip } from "antd";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import type React from "react";
import { EventReservationDuration } from "./EventReservationDuration";
import { EventStatusBadge } from "./EventStatusBadge";

dayjs.extend(isSameOrBefore);

export interface EventOccasionCardProps {
	event: {
		id?: string | number;
		title?: string;
		image?: string;
		isDisplayed?: boolean;
		maxCapacity?: number;
		sumExpected?: number;
		startDate?: string | null;
		endDate?: string | null;
		start?: string | null;
		end?: string | null;
		isSettingTime?: boolean;
		price?: number | null;
		currency?: string;
	};
	primaryColor?: string;
	primaryLightColor?: string;
	imageBaseUrl?: string;
	onClick?: () => void;
}

export const EventOccasionCard: React.FC<EventOccasionCardProps> = ({
	event,
	primaryColor = "#99CA29",
	primaryLightColor = "#fdfff0",
	imageBaseUrl = "",
	onClick,
}) => {
	const customGreen = "#8ac926";
	const customRed = "#ff595e";

	const isActive = (endDate?: string | null) => {
		if (!endDate) return false;
		try {
			const end = dayjs(endDate);
			const now = dayjs();
			return now.isSameOrBefore(end, "minute");
		} catch (error) {
			console.warn("Error parsing date:", endDate, error);
			return false;
		}
	};

	const shouldBeGrayscale =
		!isActive(event.isSettingTime ? event.endDate : event.end) ||
		!event.isDisplayed;

	const getImageUrl = (imagePath?: string) => {
		if (!imagePath) return "/no-image.png";
		if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
			return imagePath;
		}
		return `${imageBaseUrl}${imagePath}`;
	};

	return (
		<Badge.Ribbon
			text={
				event.isDisplayed ? (
					<p style={{ margin: 0 }}>
						<EyeOutlined />
						<span style={{ marginLeft: "4px" }}>表示中</span>
					</p>
				) : (
					<p style={{ margin: 0 }}>
						<EyeInvisibleOutlined />
						<span style={{ marginLeft: "4px" }}>非表示中</span>
					</p>
				)
			}
			color={event.isDisplayed ? customGreen : customRed}
		>
			<Card
				hoverable
				style={{
					cursor: onClick ? "pointer" : "default",
					filter: shouldBeGrayscale ? "grayscale(100%)" : "none",
				}}
				cover={
					<Image
						preview={false}
						src={getImageUrl(event.image)}
						fallback="/no-image.png"
						height={350}
						width="100%"
						style={{ objectFit: "contain" }}
						alt={event.title || "イベント画像"}
					/>
				}
				onClick={onClick}
			>
				<Tooltip title={event.title || ""}>
					<div
						style={{
							display: "-webkit-box",
							WebkitLineClamp: 2,
							WebkitBoxOrient: "vertical",
							overflow: "hidden",
							textOverflow: "ellipsis",
							wordBreak: "break-word",
							textAlign: "center",
							fontSize: "16px",
							fontWeight: "bold",
							height: "48px",
							lineHeight: "24px",
							marginBottom: "16px",
						}}
					>
						{event.title || ""}
					</div>
				</Tooltip>
				<Divider style={{ margin: "32px 0" }}>予約状況</Divider>

				<EventStatusBadge
					maxCapacity={event.maxCapacity}
					sumExpected={event.sumExpected}
					primaryColor={primaryColor}
					primaryLightColor={primaryLightColor}
				/>

				{event.price !== undefined &&
					event.price !== null &&
					event.price > 0 && (
						<>
							<Divider style={{ margin: "32px 0" }}>参加費</Divider>
							<div
								style={{
									textAlign: "center",
									fontSize: "20px",
									fontWeight: "bold",
									color: primaryColor,
								}}
							>
								{formatPrice(event.price)}
							</div>
						</>
					)}

				{event.isSettingTime ? (
					<>
						<Divider style={{ margin: "32px 0" }}>
							予約期間・イベント期間
						</Divider>
						<EventReservationDuration
							start={event.startDate}
							end={event.endDate}
						/>
					</>
				) : (
					<>
						<Divider style={{ margin: "32px 0" }}>予約期間（自動計算）</Divider>
						<EventReservationDuration start={event.start} end={event.end} />
					</>
				)}
			</Card>
		</Badge.Ribbon>
	);
};
